import React, { useEffect, useRef, useState } from "react";

import { isNullOrUndefined, isWithinBounds, useGetFootprintingDairyHerdDetails, useMutateFootprintingDairyHerdDetails, useTranslation } from "utils";

import { ENUM_FARM_FOOTPRINTING_DAIRY_CALVES_FEED, FARM_FOOTPRINTING_DAIRY_CALVES_FEED } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, Option, PageSpin, Select } from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const HerdOffspringForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [selectedCalvesFeed, setSelectedCalvesFeed] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_CALVES_FEED | null>(null);
  const [ageOfFirstCalvingMonths, setAgeOfFirstCalvingMonths] = useState<number | null>(null);
  const [timeBetweenCalvingDays, setTimeBetweenCalvingDays] = useState<number | null>(null);
  const [hasTimeBetweenError, setHasTimeBetweenError] = useState(false);

  const { data: dairyHerdDetails, isSuccess, isLoading } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "");

  const { mutate: mutateDairyHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      selectedCalvesFeed,
      ageOfFirstCalvingMonths,
      timeBetweenCalvingDays,
    },
    primaryDataId
  );

  useEffect(() => {
    if (dairyHerdDetails && isSuccess) {
      setSelectedCalvesFeed(dairyHerdDetails.selectedCalvesFeed ?? null);
      setAgeOfFirstCalvingMonths(dairyHerdDetails.ageOfFirstCalvingMonths ?? null);
      setTimeBetweenCalvingDays(dairyHerdDetails.timeBetweenCalvingDays ?? null);

      formRef.current?.setFieldsValue({
        selectedCalvesFeed: dairyHerdDetails.selectedCalvesFeed ?? null,
        ageOfFirstCalvingMonths: dairyHerdDetails.ageOfFirstCalvingMonths ?? null,
        timeBetweenCalvingDays: dairyHerdDetails.timeBetweenCalvingDays ?? null,
      });

      const fieldsToValidate = (["selectedCalvesFeed", "ageOfFirstCalvingMonths", "timeBetweenCalvingDays"] as const).some(
        (field) => dairyHerdDetails[field] !== null && dairyHerdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyHerdDetails, isSuccess]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyHerdDetails();
      }
    },
    [isLoading, mutateDairyHerdDetails, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  const handleFieldsChange = () => {
    const timeBetweenErrors = formRef.current?.getFieldError("timeBetweenCalvingDays") || [];
    setHasTimeBetweenError(timeBetweenErrors.length > 0);
  };

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyHerdOffspringForm.title")}</h1>
      <Card dataTestId="footprinting-farm-soil-form" className="margin-bottom-100 padding-200">
        <Form ref={formRef} onFieldsChange={handleFieldsChange}>
          <Flex vertical>
            <div className="margin-bottom-50">
              {/* @ts-expect-error need to convert to tsx */}
              <Select
                name="selectedCalvesFeed"
                disabled={readOnly}
                dataTestId="calves-feed-select"
                label={t("dairyHerdOffspringForm.question.selectedCalvesFeedInput")}
                rules={[{ required: true, message: false }]}
                initialValue={selectedCalvesFeed}
                value={selectedCalvesFeed}
                onChange={(v) => setSelectedCalvesFeed(v)}
                dropdownStyle={{ zIndex: 10010 }}
              >
                {Object.values(FARM_FOOTPRINTING_DAIRY_CALVES_FEED).map((type) => (
                  <Option
                    disabled={readOnly}
                    key={`calves-feed-${type}`}
                    data-testid={`calves-feed-${type}`}
                    label={t(`dairyHerdOffspringForm.calvesFeedOptions.${type}`)}
                    value={type}
                  >
                    {t(`dairyHerdOffspringForm.calvesFeedOptions.${type}`)}
                  </Option>
                ))}
              </Select>
              <p className="paragraph-s grey-600-text margin-top-25 margin-bottom-25 margin-left-75">
                {t("dairyHerdOffspringForm.calvesFeedSuggestionMessage")}
              </p>
            </div>

            <Form.Item>
              <InputNumber
                className="floating-label margin-bottom-150"
                name="ageOfFirstCalvingMonths"
                disabled={readOnly}
                dataTestId="footprinting-dairy-age-of-first-calving-input"
                label={t("dairyHerdOffspringForm.question.ageOfFirstCalvingInput")}
                suffix={t("dairyHerdOffspringForm.question.ageOfFirstCalvingSuffix")}
                value={ageOfFirstCalvingMonths}
                initialValue={ageOfFirstCalvingMonths}
                onChange={(v) => setAgeOfFirstCalvingMonths(v)}
                validateFirst
                rules={[
                  { required: true, message: t("dairyHerdOffspringForm.error.validNumberMonths") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 18, 60)) {
                        return Promise.reject(new Error(t("dairyHerdOffspringForm.error.validNumberMonths")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
            <Form.Item className={hasTimeBetweenError ? "padding-bottom-200" : ""}>
              <InputNumber
                className="floating-label"
                name="timeBetweenCalvingDays"
                disabled={readOnly}
                dataTestId={"footprinting-farm-dairy-time-between-calving-input"}
                label={t("dairyHerdOffspringForm.question.timeBetweenCalvingInput")}
                suffix={t("dairyHerdOffspringForm.question.timeBetweenCalvingSuffix")}
                value={timeBetweenCalvingDays}
                initialValue={timeBetweenCalvingDays}
                onChange={(v) => setTimeBetweenCalvingDays(v)}
                validateFirst
                rules={[
                  { required: true, message: t("dairyHerdOffspringForm.error.validNumberDays") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isWithinBounds(v, 300, 450)) {
                        return Promise.reject(new Error(t("dairyHerdOffspringForm.error.validNumberDays")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
          </Flex>
        </Form>
      </Card>
    </div>
  );
};
